import moment from 'moment';
import * as Yup from 'yup';

export type DatePresetKey =
  | 'priorMonth'
  | 'monthToDate'
  | 'last30'
  | 'last60'
  | 'last90';

export interface DatePreset {
  key: DatePresetKey;
  label: string;
  getRange: () => { from: Date; to: Date };
}

export const formatDateMMDDYYYY = (date: Date) => {
  if (isNaN(date.getTime())) {
    return '';
  }
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

export const buildDatePresets = (): DatePreset[] => {
  const today = new Date();

  return [
    {
      key: 'priorMonth',
      label: 'Prior Month',
      getRange: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return { from: start, to: end };
      },
    },
    {
      key: 'monthToDate',
      label: 'Month to Date',
      getRange: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: start, to: new Date() };
      },
    },
    {
      key: 'last30',
      label: 'Last 30 Days',
      getRange: () => {
        const end = new Date();
        const start = new Date(end);
        start.setDate(end.getDate() - 30);
        return { from: start, to: end };
      },
    },
    {
      key: 'last60',
      label: 'Last 60 Days',
      getRange: () => {
        const end = new Date();
        const start = new Date(end);
        start.setDate(end.getDate() - 60);
        return { from: start, to: end };
      },
    },
    {
      key: 'last90',
      label: 'Last 90 Days',
      getRange: () => {
        const end = new Date();
        const start = new Date(end);
        start.setDate(end.getDate() - 90);
        return { from: start, to: end };
      },
    },
  ];
};

export const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 30);
  return { from: start, to: end };
};

export const buildDefaultDateFilters = () => {
  const { from, to } = getDefaultDateRange();
  return {
    rangefrom: formatDateMMDDYYYY(from),
    rangeto: formatDateMMDDYYYY(to),
  };
};

export const normalizeDateString = (value?: string) => {
  if (!value) {
    return '';
  }
  const parsed = moment(value, ["MM/DD/YYYY", moment.ISO_8601], true);
  if (!parsed.isValid()) {
    return '';
  }
  return parsed.format('MM/DD/YYYY');
};

export const isPresetMatch = (presetRange: { from: Date; to: Date }, fromValue: string, toValue: string) => {
  const formattedFrom = formatDateMMDDYYYY(presetRange.from);
  const formattedTo = formatDateMMDDYYYY(presetRange.to);
  return formattedFrom === fromValue && formattedTo === toValue;
};

export const getPresetFromValues = (presets: DatePreset[], fromValue?: string, toValue?: string): DatePresetKey | null => {
  const normalizedFrom = normalizeDateString(fromValue);
  const normalizedTo = normalizeDateString(toValue);

  if (!normalizedFrom || !normalizedTo) {
    return null;
  }

  for (const preset of presets) {
    if (isPresetMatch(preset.getRange(), normalizedFrom, normalizedTo)) {
      return preset.key;
    }
  }

  return null;
};

export const dateStringValidator = () =>
  Yup.string()
    .required('Required!')
    .nullable()
    .test('is-valid-date', 'Invalid date format', (value) =>
      value ? moment(value, 'MM/DD/YYYY', true).isValid() : true,
    );
